// Form rules for the Trade licence section of the Add/Edit Client dialog (28-organization-documents SPEC.md §5).

export const EMPTY_LICENCE = {
  licenceNumber: '',
  tradeName: '',
  tradeNameArabic: '',
  legalForm: '',
  issuingAuthority: '',
  emirate: '',
  registerNumber: '',
  chamberNumber: '',
  issueDate: '',
  expiryDate: '',
  address: '',
  owners: [],
  managers: [],
  activities: [],
  source: 'MANUAL',
  extractedRaw: null,
};

const text = (v) => (v === null || v === undefined ? '' : String(v));
const rows = (list, keys) => (Array.isArray(list) ? list : []).map((r) => Object.fromEntries(keys.map((k) => [k, text(r?.[k])])));

export const OWNER_KEYS = ['name', 'nationality', 'sharePercent', 'personNumber'];
export const MANAGER_KEYS = ['name', 'nationality', 'personNumber'];
export const ACTIVITY_KEYS = ['code', 'name'];

// A stored licence (GET) or an extraction → form values (strings, never null).
export const toLicenceForm = (data, source) => ({
  ...EMPTY_LICENCE,
  ...Object.fromEntries(Object.keys(EMPTY_LICENCE)
    .filter((k) => !['owners', 'managers', 'activities', 'source', 'extractedRaw'].includes(k))
    .map((k) => [k, text(data?.[k])])),
  owners: rows(data?.owners, OWNER_KEYS),
  managers: rows(data?.managers, MANAGER_KEYS),
  activities: rows(data?.activities, ACTIVITY_KEYS),
  source: source || data?.source || 'MANUAL',
  extractedRaw: null,
});

export const fromExtraction = (extracted) => ({ ...toLicenceForm(extracted, 'IMPORTED'), extractedRaw: extracted });

// Client fields the licence can fill: only empty ones are filled; the rest are offered as suggestions.
const CLIENT_FIELDS = {
  name: 'tradeName', trnNumber: 'trnNumber', contactPerson: 'contactPerson', contactNumber: 'contactNumber',
  email: 'email', emirate: 'emirate', address: 'address', country: 'country',
};

export const applyToClient = (clientForm, extracted) => {
  const form = { ...clientForm };
  const suggestions = {};
  Object.entries(CLIENT_FIELDS).forEach(([field, source]) => {
    const value = text(extracted?.[source]).trim();
    if (!value) return;
    if (!text(form[field]).trim()) form[field] = value;
    else if (form[field] !== value) suggestions[field] = value;
  });
  return { form, suggestions };
};

const DAY = 24 * 60 * 60 * 1000;

// today: 'YYYY-MM-DD'. Expired, or expiring within 30 days; otherwise null.
export const expiryStatus = (expiryDate, today) => {
  if (!expiryDate) return null;
  const days = Math.round((new Date(`${expiryDate}T00:00:00Z`) - new Date(`${today}T00:00:00Z`)) / DAY);
  if (Number.isNaN(days)) return null;
  if (days < 0) return { label: 'Expired', color: 'error' };
  if (days <= 30) return { label: days === 0 ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`, color: 'warning' };
  return null;
};

const filled = (r) => Object.values(r).some((v) => text(v).trim());

export const hasLicence = (licence) => Object.entries(licence || {})
  .filter(([k]) => !['source', 'extractedRaw'].includes(k))
  .some(([, v]) => (Array.isArray(v) ? v.some(filled) : text(v).trim() !== ''));

const orNull = (v) => (text(v).trim() === '' ? null : text(v).trim());
const cleanRows = (list, keys, numeric = []) => list.filter(filled).map((r) => Object.fromEntries(keys.map((k) => [
  k, numeric.includes(k) ? (orNull(r[k]) === null ? null : Number(String(r[k]).replace('%', ''))) : orNull(r[k]),
])));

// Body for PUT .../trade-licence (camelCase; the service converts to snake_case). Empty → {} (deletes).
export const licencePayload = (licence) => {
  if (!hasLicence(licence)) return {};
  return {
    licenceNumber: orNull(licence.licenceNumber),
    tradeName: orNull(licence.tradeName),
    tradeNameArabic: orNull(licence.tradeNameArabic),
    legalForm: orNull(licence.legalForm),
    issuingAuthority: orNull(licence.issuingAuthority),
    emirate: orNull(licence.emirate),
    registerNumber: orNull(licence.registerNumber),
    chamberNumber: orNull(licence.chamberNumber),
    issueDate: orNull(licence.issueDate),
    expiryDate: orNull(licence.expiryDate),
    owners: cleanRows(licence.owners, OWNER_KEYS, ['sharePercent']),
    managers: cleanRows(licence.managers, MANAGER_KEYS),
    activities: cleanRows(licence.activities, ACTIVITY_KEYS),
    source: licence.source || 'MANUAL',
  };
};

// What the scan said, kept as read-only history on the stored licence document.
export const licenceMetadata = (licence) => (licence?.extractedRaw ? JSON.stringify(licence.extractedRaw) : undefined);

const messageOf = (e, fallback) => e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;

// Client first, then the licence file (when one was imported), then the profile. A failure after the client
// keeps it saved; `pending` says what is still to do, so a retry updates the client and skips finished steps.
export const saveClientWithLicence = async ({ saveClient, saveDocument, saveLicence, licence, pending }) => {
  let savedClient;
  try {
    savedClient = await saveClient();
  } catch (e) {
    return { savedClient: null, error: messageOf(e, 'Could not save the client.'), pending };
  }
  const left = { ...pending };
  const steps = [['document', () => saveDocument(savedClient.id)], ['profile', () => saveLicence(savedClient.id, licencePayload(licence))]];
  for (const [step, run] of steps) {
    if (left[step]) {
      try {
        await run();
        left[step] = false;
      } catch (e) {
        return { savedClient, error: `Client saved — trade licence couldn't be saved: ${messageOf(e, 'unknown error')}`, pending: left };
      }
    }
  }
  return { savedClient, error: null, pending: left };
};
